// Settings dialog — drives the <dialog> shell, adapter selection, and data ops.

import {
    getAllRides, getRideCount, getLatestRideDate,
    clearAllRides, getMeta, setMeta
} from './db.js';
import fileImportAdapter from './adapters/fileImport.js';
import wahooAdapter from './adapters/wahoo.js';
import customUrlAdapter from './adapters/customUrl.js';
import demoDataAdapter from './adapters/demoData.js';
import { getUserName, setUserName } from './userName.js';
import { telemetryAllowed, setTelemetry, telemetryForcedOffReason } from './telemetry.js';
import { ingestLoad } from './dataLoader.js';

const adapters = [fileImportAdapter, wahooAdapter, customUrlAdapter, demoDataAdapter];

let _onDataChange = null;
let _onUserNameChange = null;
let _activeAdapter = null;

export function initSettings({ onDataChange, onUserNameChange } = {}) {
    _onDataChange = onDataChange;
    _onUserNameChange = onUserNameChange;
    _renderAdapterTabs();
    _markActiveTab();
    _bindDataManagementButtons();
    _bindDialogControls();
    _bindUserNameField();
    _bindTelemetryToggle();
}

// Mark the tab of the active source (ADR-0005) so it's clear at a glance which
// adapter owns ongoing ingest. Cheap and independent of config-render timing.
async function _markActiveTab() {
    const activeId = await getMeta('activeAdapter');
    document.querySelectorAll('.adapter-tab').forEach(tab => {
        tab.classList.toggle('is-active-source', tab.dataset.adapterId === activeId);
    });
}

export async function openSettings(adapterId = null) {
    const dialog = document.getElementById('settings');
    if (!dialog) return;
    // Resolve the display-name input value before the dialog becomes interactive,
    // otherwise the async refresh can clobber a value the user has already typed.
    await _refreshUserNameField();
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('.settings-dialog__body')?.focus({ preventScroll: true });
    if (adapterId) {
        requestAnimationFrame(() => _selectAdapter(adapterId));
    } else {
        requestAnimationFrame(async () => {
            const persisted = await getMeta('activeAdapter');
            if (persisted && !_isActiveRendered(persisted)) {
                _selectAdapter(persisted);
            } else if (!persisted && !document.querySelector('.adapter-tab.active')) {
                // No active source (fresh install, disconnected, or legacy
                // migration gap): default to the first tab so the dialog never
                // opens with an empty config pane.
                _selectAdapter(adapters[0].id);
            }
        });
    }
    _markActiveTab();
    _refreshDataStats();
}

function _isActiveRendered(adapterId) {
    const activeTab = document.querySelector('.adapter-tab.active');
    const configEl = document.getElementById('adapter-config');
    return activeTab?.dataset.adapterId === adapterId && configEl?.childElementCount > 0;
}

function _bindDialogControls() {
    const dialog = document.getElementById('settings');
    if (!dialog) return;

    dialog.querySelector('.settings-close')?.addEventListener('click', () => dialog.close());

    // Close on backdrop click (click target is the <dialog> itself, not content)
    dialog.addEventListener('click', e => {
        if (e.target === dialog) dialog.close();
    });
}

// ── Adapter tabs ──────────────────────────────────────────────────────────────

function _renderAdapterTabs() {
    const container = document.getElementById('adapter-tabs');
    if (!container) return;

    container.innerHTML = adapters.map(a => `
        <button class="adapter-tab" data-adapter-id="${a.id}" role="tab" type="button">${a.name}</button>
    `).join('');

    container.querySelectorAll('.adapter-tab').forEach(tab => {
        tab.addEventListener('click', () => _selectAdapter(tab.dataset.adapterId));
        tab.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                _selectAdapter(tab.dataset.adapterId);
            }
        });
    });

}

function _renderAdapterInfo(adapter) {
    const el = document.getElementById('adapter-info');
    if (!el) return;
    el.textContent = adapter?.infoText || '';
}

function _selectAdapter(adapterId) {
    document.querySelectorAll('.adapter-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.adapterId === adapterId);
    });

    // Tab clicks are pure navigation (ADR-0005); the active source is set on
    // load by ingestLoad, never by viewing a tab.

    const adapter = adapters.find(a => a.id === adapterId);

    // Tear down the previously selected adapter before swapping in the next,
    // per the adapter cleanup() contract (README) — frees any timers/listeners.
    if (_activeAdapter && _activeAdapter !== adapter) {
        _activeAdapter.cleanup?.();
    }
    _activeAdapter = adapter;

    _renderAdapterInfo(adapter);

    const configEl = document.getElementById('adapter-config');
    if (configEl) {
        configEl.innerHTML = '';
        adapter?.renderConfig(
            configEl,
            _handleAdapterComplete.bind(null, adapterId),
            _handleAdapterDisconnect.bind(null, adapterId),
        );
    }
}

// Disconnect (remove a source) ≠ delete data (ADR-0005). Tears down the
// adapter's own credentials/config and, if it was the active source, clears
// activeAdapter — but leaves the ride data intact. Re-renders the now
// disconnected config and refreshes the active-source marker.
async function _handleAdapterDisconnect(adapterId) {
    const adapter = adapters.find(a => a.id === adapterId);
    await adapter?.revokeAccess?.();
    if ((await getMeta('activeAdapter')) === adapterId) await setMeta('activeAdapter', null);

    const configEl = document.getElementById('adapter-config');
    if (configEl && adapter) {
        configEl.innerHTML = '';
        adapter.renderConfig(
            configEl,
            _handleAdapterComplete.bind(null, adapterId),
            _handleAdapterDisconnect.bind(null, adapterId),
        );
    }
    await _markActiveTab();
    await _refreshDataStats();
    _onDataChange?.();
}

async function _handleAdapterComplete(adapterId, { rides, message }) {
    const adapter = adapters.find(a => a.id === adapterId);
    const switchingIntoDemo = adapterId === 'demo-data';
    // Demo is the one exclusive source (ADR-0005): never union fake rides with
    // real data. Read the current active source by id — no separate demoActive.
    const switchingAwayFromDemo =
        (await getMeta('activeAdapter')) === 'demo-data' && !switchingIntoDemo;

    if (switchingAwayFromDemo || switchingIntoDemo) {
        await clearAllRides();
    }

    // Switching the active source does NOT disconnect the previous one (ADR-0005,
    // D1): its credentials stay so the user can switch back. Only an explicit
    // Disconnect revokes.

    await ingestLoad(adapter, { rides, message }, { trigger: 'manual' });

    if (adapter?.kind === 'sync' && adapter.getUserName) {
        const name = (await adapter.getUserName())?.trim();
        if (name) {
            await setUserName(name);
            await _refreshUserNameField();
            _onUserNameChange?.();
        }
    }

    _updateAdapterStatus(message);

    // The loaded adapter is now the active source — reflect it in the tab marker
    // and flip its action button (Activate → Sync now) without a full re-render.
    const syncBtn = document.querySelector('#adapter-config .adapter-action-sync');
    if (syncBtn) syncBtn.textContent = 'Sync now';
    await _markActiveTab();

    await _refreshDataStats();
    _onDataChange?.();

    // A load succeeded — close the dialog so the freshly rendered dashboard is
    // immediately visible. Loads only: disconnect/delete keep the dialog open.
    document.getElementById('settings')?.close();
}

// ── Data management ───────────────────────────────────────────────────────────

function _bindDataManagementButtons() {
    document.getElementById('btn-download-data')
        ?.addEventListener('click', _handleDownload);
    document.getElementById('btn-delete-data')
        ?.addEventListener('click', _handleDeleteData);
}

async function _handleDownload() {
    const rides = await getAllRides();
    const json = JSON.stringify({ tours: rides }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cycling-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

async function _handleDeleteData() {
    const confirmed = confirm(
        'This will delete all locally stored rides. Your source stays connected: ' +
        'reloading re-fetches from a sync source, but one-shot data (demo, imported ' +
        'files) and data from inactive sources will not come back.'
    );
    if (!confirmed) return;
    // Delete data only — keep the active adapter connected (ADR-0005). The reload
    // re-syncs a sync source; a oneShot source (e.g. demo) just stays empty.
    await clearAllRides();
    location.reload();
}

// ── UI helpers ────────────────────────────────────────────────────────────────

async function _refreshDataStats() {
    const count = await getRideCount();
    const latest = await getLatestRideDate();

    const countEl = document.getElementById('stat-ride-count');
    const dateEl = document.getElementById('stat-latest-date');
    if (countEl) countEl.textContent = count;
    if (dateEl) dateEl.textContent = latest || '—';
}

function _updateAdapterStatus(message) {
    const configEl = document.getElementById('adapter-config');
    const el = configEl?.querySelector('.adapter-status');
    if (el) el.textContent = message;
}

// ── Display name ──────────────────────────────────────────────────────────────

function _bindUserNameField() {
    const form = document.getElementById('user-name-form');
    if (!form) return;
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = document.getElementById('input-user-name');
        const value = input?.value.trim();
        // Empty = cancel; do not mutate the stored name.
        if (!value) {
            await _refreshUserNameField();
            return;
        }
        await setUserName(value);
        _onUserNameChange?.();
    });
}

async function _refreshUserNameField() {
    const input = document.getElementById('input-user-name');
    if (!input) return;
    input.value = await getUserName();
}

// ── Telemetry toggle ──────────────────────────────────────────────────────────

function _bindTelemetryToggle() {
    const checkbox = document.getElementById('input-telemetry');
    if (!checkbox) return;

    const reason = telemetryForcedOffReason();
    checkbox.checked = telemetryAllowed();

    if (reason) {
        // Some signals are non-overridable (GPC/DNT) or environment-derived
        // (local dev). Show why toggling is disabled instead of silently
        // ignoring the click.
        checkbox.disabled = true;
        const hint = document.getElementById('telemetry-hint');
        if (hint) hint.textContent = reason === 'local-dev'
            ? 'Disabled on local development — anonymous usage data is sent from deployed builds only.'
            : 'Disabled by your browser’s Do Not Track / Global Privacy Control signal.';
        return;
    }

    checkbox.addEventListener('change', () => {
        setTelemetry(checkbox.checked);
    });
}
